import { getGlobeProjection } from './globe-projection.js';
import type OlMap from 'ol/Map.js';
import { fromLonLat } from 'ol/proj.js';
import type { Point, Stroke } from '../types/app.js';
import { copyTextWithFallback } from './dom-binding.js';
import { t } from './i18n.js';
import { getMarkerScreenBounds } from './marker-renderer.js';
import { getRenderedWidth } from './map-math.js';
import '../styles/marker-details.css';

export interface MarkerDetails {
  id: string;
  point: Point;
  name?: string;
  address?: string;
  note?: string;
  stroke?: Stroke;
}

export function strokeMarkerDetails(strokes: Stroke[]): MarkerDetails[] {
  return strokes.filter(stroke => stroke.tool === 'marker' && stroke.points.length > 0 && stroke.opacity !== 0).map(stroke => ({
    id: stroke.id, point: stroke.points[0], name: stroke.text,
    address: stroke.markerAddress, note: stroke.markerNote, stroke,
  }));
}

export function openMarkerDetails(marker: MarkerDetails): HTMLDialogElement {
  const dialog = document.createElement('dialog');
  dialog.className = 'marker-details';
  const title = document.createElement('h2');
  title.id = 'marker-details-title';
  title.textContent = (marker.name !== marker.address ? marker.name : '') || t('marker_details.location');
  dialog.setAttribute('aria-labelledby', title.id);
  const close = document.createElement('button');
  close.type = 'button'; close.className = 'marker-details-close';
  close.textContent = '×'; close.setAttribute('aria-label', t('shell.close'));
  close.addEventListener('click', () => dialog.close());
  const header = document.createElement('header'); header.appendChild(title); header.appendChild(close);
  const address = document.createElement('textarea');
  address.className = 'marker-details-address'; address.readOnly = true;
  address.setAttribute('aria-label', t(marker.address ? 'marker_details.address' : 'search.copy_coordinates'));
  address.value = marker.address || `${marker.point.lat.toFixed(6)}, ${marker.point.lng.toFixed(6)}`;
  address.rows = Math.min(5, Math.max(1, Math.ceil(address.value.length / 36)));
  const note = document.createElement('p'); note.textContent = marker.note || ''; note.hidden = !marker.note;
  const actions = document.createElement('footer');
  const copy = document.createElement('button'); copy.type = 'button';
  copy.textContent = t(marker.address ? 'search.copy_address' : 'search.copy_coordinates');
  const status = document.createElement('p'); status.className = 'marker-details-status'; status.setAttribute('role', 'status');
  copy.addEventListener('click', () => {
    void copyTextWithFallback(address.value, address).then(
      () => { status.textContent = t('search.copied'); copy.focus(); },
      () => { status.textContent = t('search.copy_failed'); },
    );
  });
  const directions = document.createElement('a');
  directions.textContent = t('marker_details.directions');
  const url = new URL('https://www.google.com/maps/dir/');
  url.searchParams.set('api', '1');
  url.searchParams.set('destination', `${marker.point.lat},${marker.point.lng}`);
  directions.href = url.href; directions.target = '_blank'; directions.rel = 'noopener noreferrer';
  actions.appendChild(copy); actions.appendChild(directions);
  for (const child of [header, address, note, actions, status]) dialog.appendChild(child);
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => dialog.remove(), { once: true });
  document.body.appendChild(dialog); dialog.showModal();
  return dialog;
}

/** Canvas markers keep their map gestures; transparent controls provide keyboard access. */
export function installMarkerDetails(map: OlMap, markers: () => MarkerDetails[], enabled = () => true) {
  const host = map.getTargetElement();
  if (!host) throw new Error('Map target is not available.');
  const controls = document.createElement('div'); controls.className = 'marker-details-controls';
  host.appendChild(controls);
  const buttons = new Map<string, HTMLButtonElement>();
  let dialog: HTMLDialogElement | null = null;
  const open = (marker: MarkerDetails) => {
    dialog?.close(); dialog = openMarkerDetails(marker);
  };
  const bounds = (marker: MarkerDetails) => {
    const globe = getGlobeProjection();
    const projected = globe?.project(marker.point);
    const pixel = globe ? (projected ? [projected.x, projected.y] : null)
      : map.getPixelFromCoordinate(fromLonLat([marker.point.lng, marker.point.lat]));
    if (!pixel) return null;
    const rect = marker.stroke ? getMarkerScreenBounds({ x: pixel[0], y: pixel[1] }, getRenderedWidth(marker.stroke, globe?.zoom() ?? map.getView().getZoom() ?? 13), marker.stroke.markerIcon)
      : { minX: pixel[0] - 7, maxX: pixel[0] + 7, minY: pixel[1] - 7, maxY: pixel[1] + 7 };
    return { x: (rect.minX + rect.maxX) / 2, y: (rect.minY + rect.maxY) / 2, width: Math.max(44, rect.maxX - rect.minX), height: Math.max(44, rect.maxY - rect.minY) };
  };
  const refresh = () => {
    const size = map.getSize();
    const current = enabled() && size ? markers().filter(marker => {
      const rect = bounds(marker);
      return rect && rect.x >= 0 && rect.y >= 0 && rect.x <= size[0] && rect.y <= size[1];
    }) : [];
    const ids = new Set(current.map(marker => marker.id));
    for (const [id, button] of buttons) if (!ids.has(id)) { button.remove(); buttons.delete(id); }
    for (const marker of current) {
      let button = buttons.get(marker.id);
      if (!button) {
        button = document.createElement('button'); button.type = 'button';
        button.className = 'marker-details-target'; button.dataset.markerId = marker.id;
        button.addEventListener('click', event => {
          event.stopPropagation();
          const selected = markers().find(item => item.id === marker.id);
          if (enabled() && selected) open(selected);
        });
        controls.appendChild(button); buttons.set(marker.id, button);
      }
      button.setAttribute('aria-label', marker.name || marker.address || t('marker_details.location'));
      const rect = bounds(marker);
      button.hidden = !rect || !size || rect.x < 0 || rect.y < 0 || rect.x > size[0] || rect.y > size[1];
      if (rect) Object.assign(button.style, { left: `${rect.x}px`, top: `${rect.y}px`, width: `${rect.width}px`, height: `${rect.height}px` });
    }
  };
  map.on('postrender', refresh);
  window.addEventListener('globe-view-changed', refresh);
  window.addEventListener('globe-camera-changed', refresh);
  host.addEventListener('focusin', refresh);
  let start: { x: number; y: number } | null = null;
  let dragged = false;
  host.addEventListener('pointerdown', event => { start = { x: event.clientX, y: event.clientY }; dragged = false; });
  host.addEventListener('pointermove', event => { if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) dragged = true; });
  host.addEventListener('pointercancel', () => { dragged = true; });
  host.addEventListener('click', event => {
    if (!enabled() || dragged || (event.target instanceof Element && event.target.closest('.marker-details-target, button, a, input, textarea'))) return;
    start = null;
    const hostRect = host.getBoundingClientRect();
    const pixel = [event.clientX - hostRect.left, event.clientY - hostRect.top];
    const marker = [...markers()].reverse().find(item => {
      const rect = bounds(item);
      return rect && Math.abs(pixel[0] - rect.x) <= rect.width / 2 && Math.abs(pixel[1] - rect.y) <= rect.height / 2;
    });
    if (marker) open(marker);
  });
  return { refresh, close: () => { dialog?.close(); } };
}
